import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '../../../components';
import { Project, Setting, SignalContact, User } from '../../../models';
import { SignalContacts } from '../../../services';
import {
    RootState, useAppDispatch, useAppSelector,
    loadProjects, loadUsers, loadSetting, updateSetting,
} from '../../../redux';

function contactLabel(contact: SignalContact, usersById: Map<string, User>) {
    const linked = contact.userId ? usersById.get(contact.userId) : null;
    const linkedName = linked
        ? [linked.firstname, linked.lastname].filter(Boolean).join(' ').trim()
        : '';
    const name = (typeof contact.name === 'string' && contact.name && !contact.name.includes('[object Object]'))
        ? contact.name
        : (linkedName || null);
    if (name)
        return `${name} (${contact._id})`;
    return contact._id;
}

function SignalContactsSection() {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const { projects } = useAppSelector((state: RootState) => state.projects);
    const { users } = useAppSelector((state: RootState) => state.users);
    const { user } = useAppSelector((state: RootState) => state.auth);
    const { setting } = useAppSelector((state: RootState) => state.settings);
    const [contacts, setContacts] = useState<SignalContact[]>([]);
    const [loading, setLoading] = useState(false);
    const [savingId, setSavingId] = useState<string | null>(null);
    const [trustingId, setTrustingId] = useState<string | null>(null);
    const [savingFallbackProject, setSavingFallbackProject] = useState(false);
    const [syncing, setSyncing] = useState(false);
    const [adding, setAdding] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [addContactId, setAddContactId] = useState('');
    const [addProjectId, setAddProjectId] = useState('');

    const refreshContacts = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const { contacts: list } = await SignalContacts.listContacts();
            setContacts(list || []);
        } catch (err) {
            setError(err instanceof Error ? err.message : String(err));
        } finally {
            setLoading(false);
        }
    }, []);

    const syncContacts = async () => {
        setSyncing(true);
        setError(null);
        try {
            const { contacts: list } = await SignalContacts.syncContacts();
            setContacts(list || []);
        } catch (err) {
            setError(err instanceof Error ? err.message : String(err));
        } finally {
            setSyncing(false);
        }
    };

    useEffect(() => {
        dispatch(loadProjects());
        dispatch(loadUsers());
        dispatch(loadSetting('default'));
        refreshContacts();
    }, [dispatch, refreshContacts]);

    const usersById = useMemo(() => {
        const map = new Map<string, User>();
        (users || []).forEach((user: User) => map.set(user._id, user));
        return map;
    }, [users]);

    const fallbackProjectId = setting?.signal_intake?.default_project || '';

    const isAdmin = user?.role === User.ROLE_ADMIN;

    const untrustedContacts = useMemo(() => {
        return (contacts || [])
            .filter((contact) => contact.identityStatus === 'untrusted')
            .slice()
            .sort((a, b) => contactLabel(a, usersById).localeCompare(contactLabel(b, usersById)));
    }, [contacts, usersById]);

    const configuredContacts = useMemo(() => {
        return (contacts || [])
            .filter((contact) => !!contact.defaultProject)
            .slice()
            .sort((a, b) => contactLabel(a, usersById).localeCompare(contactLabel(b, usersById)));
    }, [contacts, usersById]);

    const availableContacts = useMemo(() => {
        return (contacts || [])
            .filter((contact) => !contact.defaultProject)
            .slice()
            .sort((a, b) => contactLabel(a, usersById).localeCompare(contactLabel(b, usersById)));
    }, [contacts, usersById]);

    const projectOptions = useMemo(() => {
        const list = (projects || [])
            .slice()
            .sort((a: Project, b: Project) => a.name.localeCompare(b.name));
        return list.map((project: Project) => ({
            value: project._id,
            label: project.name,
        }));
    }, [projects]);

    const trustContact = async (contactId: string) => {
        setTrustingId(contactId);
        setError(null);
        try {
            await SignalContacts.trustContact(contactId);
            await refreshContacts();
        } catch (err) {
            setError(err instanceof Error ? err.message : String(err));
        } finally {
            setTrustingId(null);
        }
    };

    const patchContact = async (contactId: string, defaultProject: string | null) => {
        setSavingId(contactId);
        setError(null);
        try {
            await SignalContacts.patchContact(contactId, { defaultProject });
            await refreshContacts();
        } catch (err) {
            setError(err instanceof Error ? err.message : String(err));
        } finally {
            setSavingId(null);
        }
    };

    const saveFallbackProject = async (projectId: string) => {
        if (!setting)
            return;
        setSavingFallbackProject(true);
        setError(null);
        try {
            const next = new Setting({
                ...(setting as AnyObj),
                signal_intake: {
                    ...(setting.signal_intake || {}),
                    default_project: projectId || '',
                },
            });
            await dispatch(updateSetting(next) as AnyObj);
        } catch (err) {
            setError(err instanceof Error ? err.message : String(err));
        } finally {
            setSavingFallbackProject(false);
        }
    };

    const addConfiguredContact = async () => {
        if (!addContactId || !addProjectId)
            return;
        setAdding(true);
        setError(null);
        try {
            await SignalContacts.patchContact(addContactId, { defaultProject: addProjectId });
            setAddContactId('');
            setAddProjectId('');
            await refreshContacts();
        } catch (err) {
            setError(err instanceof Error ? err.message : String(err));
        } finally {
            setAdding(false);
        }
    };

    const linkedUserLabel = (contact: SignalContact) => {
        if (!contact.userId)
            return null;
        const user = usersById.get(contact.userId);
        if (!user)
            return t('settings.signal_contacts_linked_user');
        const name = [user.firstname, user.lastname].filter(Boolean).join(' ').trim();
        return name || user.email || t('settings.signal_contacts_linked_user');
    };

    const selectClassName = "block w-full rounded-md border-0 py-1.5 text-gray-900 dark:text-gray-100 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6";

    return (
        <div className="mt-8">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div>
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                        {t('settings.signal_contacts_title')}
                    </h2>
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                        {t('settings.signal_contacts_desc')}
                    </p>
                </div>
                <button
                    type="button"
                    className="inline-flex shrink-0 items-center justify-center rounded-md bg-white dark:bg-gray-800 px-3 py-1.5 text-sm font-semibold text-gray-900 dark:text-gray-100 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:cursor-not-allowed disabled:opacity-50"
                    disabled={syncing || loading}
                    onClick={syncContacts}
                >
                    {syncing ? t('settings.signal_syncing') : t('settings.signal_contacts_sync')}
                </button>
            </div>
            {error && (
                <p className="mt-2 text-sm text-red-600">{error}</p>
            )}

            {untrustedContacts.length > 0 && (
                <div className="mt-4 rounded border border-amber-300 bg-amber-50 p-3 dark:border-amber-700 dark:bg-amber-950/40">
                    <p className="text-sm font-medium text-amber-900 dark:text-amber-100">
                        {t('settings.signal_contacts_untrusted_title')}
                    </p>
                    <p className="mt-1 text-xs text-amber-800 dark:text-amber-200">
                        {t('settings.signal_contacts_untrusted_desc')}
                    </p>
                    <div className="mt-3 space-y-2">
                        {untrustedContacts.map((contact) => (
                            <div key={contact._id} className="flex flex-col gap-2 rounded bg-white p-2 dark:bg-gray-900 sm:flex-row sm:items-center sm:justify-between">
                                <div className="text-sm text-gray-900 dark:text-gray-100">
                                    <span className="font-medium">{contactLabel(contact, usersById)}</span>
                                    {contact.userId && (
                                        <span className="ml-2 text-xs text-indigo-600">
                                            {linkedUserLabel(contact)}
                                        </span>
                                    )}
                                </div>
                                {isAdmin && (
                                    <button
                                        type="button"
                                        className="inline-flex items-center justify-center rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-50"
                                        disabled={trustingId === contact._id}
                                        onClick={() => trustContact(contact._id)}
                                    >
                                        {trustingId === contact._id
                                            ? t('settings.signal_contacts_trusting')
                                            : t('settings.signal_contacts_trust')}
                                    </button>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <div className="mt-4 rounded border p-3">
                <label className="mb-1 block text-xs font-medium text-gray-500 dark:text-gray-400">
                    {t('settings.signal_contacts_fallback_project')}
                </label>
                <p className="mb-2 text-xs text-gray-500 dark:text-gray-400">
                    {t('settings.signal_contacts_fallback_project_desc')}
                </p>
                <select
                    className={selectClassName}
                    value={fallbackProjectId}
                    disabled={savingFallbackProject || !setting}
                    onChange={(event) => saveFallbackProject(event.target.value)}
                >
                    <option value="">{t('settings.signal_contacts_default_project_none')}</option>
                    {projectOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                            {option.label}
                        </option>
                    ))}
                </select>
            </div>

            <div className="mt-4 rounded border p-3">
                <p className="mb-3 text-xs font-medium text-gray-500 dark:text-gray-400">
                    {t('settings.signal_contacts_add')}
                </p>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                    <div className="sm:col-span-1">
                        <label className="mb-1 block text-xs font-medium text-gray-500 dark:text-gray-400">
                            {t('settings.signal_contacts_select')}
                        </label>
                        <select
                            className={selectClassName}
                            value={addContactId}
                            disabled={loading || adding || availableContacts.length === 0}
                            onChange={(event) => setAddContactId(event.target.value)}
                        >
                            <option value="">
                                {loading
                                    ? t('settings.signal_contacts_loading')
                                    : t('settings.signal_contacts_select_placeholder')}
                            </option>
                            {availableContacts.map((contact) => (
                                <option key={contact._id} value={contact._id}>
                                    {contactLabel(contact, usersById)}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="sm:col-span-1">
                        <label className="mb-1 block text-xs font-medium text-gray-500 dark:text-gray-400">
                            {t('settings.signal_contacts_default_project')}
                        </label>
                        <select
                            className={selectClassName}
                            value={addProjectId}
                            disabled={adding}
                            onChange={(event) => setAddProjectId(event.target.value)}
                        >
                            <option value="">{t('settings.signal_contacts_default_project_none')}</option>
                            {projectOptions.map((option) => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="flex items-end">
                        <button
                            type="button"
                            className="inline-flex w-full items-center justify-center rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-50"
                            disabled={adding || !addContactId || !addProjectId}
                            onClick={addConfiguredContact}
                        >
                            {t('settings.signal_contacts_add_button')}
                        </button>
                    </div>
                </div>
            </div>

            {loading && configuredContacts.length === 0 ? (
                <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">{t('settings.signal_contacts_loading')}</p>
            ) : configuredContacts.length === 0 ? (
                <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">{t('settings.signal_contacts_empty')}</p>
            ) : (
                <div className="mt-4 space-y-3">
                    {configuredContacts.map((contact) => (
                        <div key={contact._id} className="grid grid-cols-1 gap-3 rounded border p-3 sm:grid-cols-[1fr_1fr_auto]">
                            <div className="flex flex-col justify-center gap-1 text-sm text-gray-900 dark:text-gray-100">
                                <div className="flex items-center gap-2 font-medium">
                                    <Icon type="signal" color="blue-600" className="inline"/>
                                    <span>{contactLabel(contact, usersById)}</span>
                                </div>
                                {contact.userId && (
                                    <span className="text-xs text-indigo-600">
                                        {linkedUserLabel(contact)}
                                    </span>
                                )}
                                {contact.identityStatus === 'untrusted' && (
                                    <span className="text-xs text-amber-700 dark:text-amber-300">
                                        {t('settings.signal_contacts_untrusted_badge')}
                                    </span>
                                )}
                            </div>
                            <div>
                                <label className="mb-1 block text-xs font-medium text-gray-500 dark:text-gray-400">
                                    {t('settings.signal_contacts_default_project')}
                                </label>
                                <select
                                    className={selectClassName}
                                    value={contact.defaultProject || ''}
                                    disabled={savingId === contact._id}
                                    onChange={(event) => patchContact(contact._id, event.target.value || null)}
                                >
                                    <option value="">{t('settings.signal_contacts_default_project_none')}</option>
                                    {projectOptions.map((option) => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="flex items-end">
                                <button
                                    type="button"
                                    className="inline-flex w-full items-center justify-center rounded-md px-3 py-1.5 text-sm font-semibold text-red-700 ring-1 ring-inset ring-red-200 hover:bg-red-500 disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
                                    disabled={savingId === contact._id}
                                    onClick={() => patchContact(contact._id, null)}
                                >
                                    {t('settings.signal_contacts_remove')}
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

export default SignalContactsSection;
